import { useHealthcareAccount } from "../auth/useHealthcareAccount";
import { getPartnerApp } from "../utils/partnerApp";
// src/contexts/UserContext.tsx

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

import { toast } from "sonner";

import {
  type CalendarSyncItem,
  type User,
  type UserContextType,
  type UserProfile,
  isDoctorProfile,
  isInsuranceProfile,
  isLaboratoryProfile,
  isPartnerProfile,
} from "./UserContext.types";

import {
  getProfileAvatar,
} from "./user/profile.utils";

import { useClinicalData } from "./user/useClinicalData";

export type {
  User,
  UserRole,
  UserProfile,
  GreetingStyle,
  DoctorProfile,
  PartnerProfile,
  InsuranceProfile,
  LaboratoryProfile,
  Patient,
  HealthcareProvider,
  Appointment,
  Consultation,
  ConsultationBase,
  ConsultationMode,
  ConsultationType,
  ConsultationStatus,
  CalendarSyncItem,
  DetailedPrescription,
  Prescription,
  TestOrder,
  TestPriority,
  InsuranceStatus,
  EmergencyFacility,
} from "./UserContext.types";

export {
  isDoctorProfile,
  isPartnerProfile,
  isInsuranceProfile,
  isLaboratoryProfile,
};

const UserContext = createContext<UserContextType | undefined>(
  undefined
);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({
  children,
}: UserProviderProps) => {
  const account = useHealthcareAccount();
  const { user, token, loading } = account;
  const hasCompletedOnboarding = Boolean(user && token);
  const clinical = useClinicalData(
    Boolean(user && token && !loading && getPartnerApp() === "hospital")
  );

  const login = useCallback(
    async (
      userData: User,
      authToken: string
    ): Promise<void> => {
      throw new Error("Please sign in through the connected authentication service.");
    },
    []
  );

  const logout = useCallback(
    async (): Promise<void> => {
      clinical.resetClinicalState();
      await account.logout();
    },
    [clinical.resetClinicalState, account.logout]
  );

  const updateUser = useCallback(
    (updates: Partial<User>): void => {
      throw new Error("Profile updates must be confirmed by the backend. Your changes have not been saved.");
    },
    []
  );

  const updateProfile = useCallback(
    async (
      profileUpdates: Partial<UserProfile>
    ): Promise<void> => {
      throw new Error("Profile updates are currently unavailable.");
    },
    []
  );

  const completeOnboarding = useCallback(
    async (
      profileData?: UserProfile,
      role?: User["role"]
    ): Promise<void> => {
      throw new Error("Your organisation setup could not be submitted. Please try again later.");
    },
    []
  );

  /**
   * Syncs an appointment or consultation with Google Calendar.
   *
   * The current implementation is a placeholder. Replace the resolved
   * promise with the actual Google Calendar API request.
   */
  const syncWithGoogleCalendar = useCallback(
    async (
      _item: CalendarSyncItem
    ): Promise<void> => {
      throw new Error("Calendar syncing is currently unavailable.");
    },
    []
  );

  const exportUserData = useCallback((): void => {
    let objectUrl: string | null = null;
    let link: HTMLAnchorElement | null = null;

    try {
      const exportData = {
        patients: clinical.patients,
        appointments: clinical.appointments,
        consultations: clinical.consultations,
        exportedAt: new Date().toISOString(),
      };

      const dataBlob = new Blob(
        [JSON.stringify(exportData, null, 2)],
        {
          type: "application/json",
        }
      );

      objectUrl = URL.createObjectURL(dataBlob);
      link = document.createElement("a");

      link.href = objectUrl;
      link.download = `user-data-${
        new Date().toISOString().split("T")[0]
      }.json`;

      document.body.appendChild(link);
      link.click();

      toast.success(
        "User data exported successfully"
      );
    } catch (error) {
      console.error("Failed to export user data:", error);

      toast.error("Failed to export user data");
    } finally {
      link?.remove();

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    }
  }, [
    clinical.patients,
    clinical.appointments,
    clinical.consultations,
  ]);

  const getLabName = useCallback((): string => {
    if (!user?.profile) {
      return user?.name || "Laboratory";
    }

    if (isLaboratoryProfile(user.profile)) {
      return user.profile.labName || "Laboratory";
    }

    if (isPartnerProfile(user.profile)) {
      return (
        user.profile.facilityName ||
        "Healthcare Facility"
      );
    }

    return user.name || "Laboratory";
  }, [user]);

  const getLabContactInfo = useCallback((): {
    email: string;
    phone: string;
    address: string;
  } => {
    if (
      user?.profile &&
      (isLaboratoryProfile(user.profile) ||
        isPartnerProfile(user.profile))
    ) {
      return {
        email:
          user.profile.email ||
          user.email ||
          "",
        phone: user.profile.phone || "",
        address: user.profile.address || "",
      };
    }

    return {
      email: user?.email || "",
      phone: "",
      address: "",
    };
  }, [user]);

  const isLabUser = useCallback((): boolean => {
    return user?.role === "laboratory";
  }, [user?.role]);

  const contextValue = useMemo<UserContextType>(
    () => ({
      user,
      token,

      isAuthenticated: Boolean(user && token),

      hasCompletedOnboarding,
      loading,

      isLoadingAppointments:
        clinical.isLoadingAppointments,

      isLoadingPatients:
        clinical.isLoadingPatients,

      patients: clinical.patients,
      appointments: clinical.appointments,
      consultations: clinical.consultations,

      login,
      logout,
      updateProfile,
      updateUser,
      completeOnboarding,

      refreshData: clinical.refreshData,

      fetchPatients: clinical.fetchPatients,

      fetchAppointments:
        clinical.fetchAppointments,

      fetchConsultations:
        clinical.fetchConsultations,

      addPatient: clinical.addPatient,

      updatePatient: clinical.updatePatient,

      deletePatient: clinical.deletePatient,

      addAppointment: clinical.addAppointment,

      updateAppointment:
        clinical.updateAppointment,

      deleteAppointment:
        clinical.deleteAppointment,

      updateAppointmentStatus:
        clinical.updateAppointmentStatus,

      addConsultation:
        clinical.addConsultation,

      updateConsultation:
        clinical.updateConsultation,

      deleteConsultation:
        clinical.deleteConsultation,

      sharePrescription:
        clinical.sharePrescription,

      shareTestOrder:
        clinical.shareTestOrder,

      syncWithGoogleCalendar,

      getLabName,
      getLabContactInfo,
      isLabUser,

      clearAllData: clinical.clearAllData,

      resetToDefaults:
        clinical.resetToDefaults,

      exportUserData,
    }),
    [
      user,
      token,
      hasCompletedOnboarding,
      loading,

      clinical.isLoadingAppointments,
      clinical.isLoadingPatients,

      clinical.patients,
      clinical.appointments,
      clinical.consultations,

      clinical.refreshData,
      clinical.fetchPatients,
      clinical.fetchAppointments,
      clinical.fetchConsultations,

      clinical.addPatient,
      clinical.updatePatient,
      clinical.deletePatient,

      clinical.addAppointment,
      clinical.updateAppointment,
      clinical.deleteAppointment,
      clinical.updateAppointmentStatus,

      clinical.addConsultation,
      clinical.updateConsultation,
      clinical.deleteConsultation,

      clinical.sharePrescription,
      clinical.shareTestOrder,

      clinical.clearAllData,
      clinical.resetToDefaults,

      login,
      logout,
      updateProfile,
      updateUser,
      completeOnboarding,

      syncWithGoogleCalendar,

      getLabName,
      getLabContactInfo,
      isLabUser,

      exportUserData,
    ]
  );

  return (
    <UserContext.Provider value={contextValue}>
      {account.error ? (
        <main className="mx-auto max-w-lg space-y-4 p-6">
          <h1 className="text-xl font-semibold text-[#17313A]">Unable to connect your account</h1>
          <p role="alert">{account.error}</p>
          <button type="button" onClick={() => void account.reconnect().catch(() => {})}>Try again</button>
          <button type="button" onClick={() => void account.logout().catch(() => {})}>Sign out</button>
        </main>
      ) : children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error(
      "useUser must be used within a UserProvider"
    );
  }

  return context;
};

export const getUserInitials = (
  name: string
): string => {
  const words = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return "U";
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();
};

export const getRoleDisplayName = (
  role: User["role"]
): string => {
  const roleMap: Record<User["role"], string> = {
    doctor: "Doctor",
    partner: "Healthcare Partner",
    insurance: "Insurance Provider",
    laboratory: "Laboratory",
  };

  return roleMap[role];
};
